import type { SavingsEvent } from "@/api/hooks/use-billing-statements";
import { formatUsd, formatTimestamp } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

interface EventDetailPanelProps {
  event: SavingsEvent;
}

export function EventDetailPanel({ event }: EventDetailPanelProps) {
  return (
    <div className="space-y-5 text-xs">
      <Section title="Overview">
        <Row label="Event ID" value={event.id} mono />
        <Row label="Request ID" value={event.request_log_id} mono />
        <Row label="Created" value={formatTimestamp(event.created_at)} mono />
        <Row label="Methodology" value={event.methodology_version} />
        <div className="flex items-center justify-between py-1">
          <span className="text-fg-muted">Confidence</span>
          <StatusBadge status={event.confidence} />
        </div>
        {event.exclusion_reason && (
          <Row label="Exclusion reason" value={event.exclusion_reason} />
        )}
      </Section>

      <Section title="Baseline">
        <Row label="Model" value={event.baseline_model} mono />
        <Row label="Input tokens" value={event.baseline_input_tokens.toLocaleString()} mono />
        <Row label="Output tokens" value={event.baseline_output_tokens.toLocaleString()} mono />
        <Row label="Cost" value={formatUsd(event.baseline_cost)} mono />
      </Section>

      <Section title="Actual">
        <Row label="Model" value={event.actual_model} mono />
        <Row label="Input tokens" value={event.actual_input_tokens.toLocaleString()} mono />
        <Row label="Output tokens" value={event.actual_output_tokens.toLocaleString()} mono />
        <Row label="Cost" value={formatUsd(event.actual_cost)} mono />
      </Section>

      <Section title="Savings">
        <Row label="Gross" value={formatUsd(event.gross_savings)} mono />
        <Row label="Optimizer overhead" value={formatUsd(event.optimizer_overhead_cost)} mono />
        <Row label="Net" value={formatUsd(event.net_savings)} mono />
        <Row
          label="Verified"
          value={formatUsd(event.verified_savings)}
          mono
          valueClass="text-safe-green"
        />
      </Section>

      {event.optimizations_applied && Object.keys(event.optimizations_applied).length > 0 && (
        <Section title="Optimizations applied">
          {Object.entries(event.optimizations_applied)
            .filter(([, value]) => typeof value !== "object" || value === null)
            .map(([key, value]) => (
              <Row
                key={key}
                label={key}
                value={typeof value === "string" ? formatUsd(value) : String(value)}
                mono
              />
            ))}
        </Section>
      )}

      {event.pricing_snapshot && (
        <Section title="Pricing snapshot">
          <pre className="rounded border border-line-subtle bg-canvas p-3 text-[11px] text-fg overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(event.pricing_snapshot, null, 2)}
          </pre>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
  valueClass = "",
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClass?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <span className="text-fg-muted shrink-0">{label}</span>
      <span
        className={`text-fg text-right break-all ${mono ? "font-mono" : ""} ${valueClass}`}
      >
        {value}
      </span>
    </div>
  );
}
